<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import types from 'dashboard/store/mutation-types';
import MenuItem from 'dashboard/components/widgets/conversation/contextMenu/menuItem.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MasifiWhatsappQrMessagesAPI from 'dashboard/api/masifi/masifiWhatsappQrMessages';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import {
  MASIFI_WA_MAX_TEXT,
  MASIFI_WA_QUICK_REACTIONS,
  canDeleteForEveryone,
  canEdit,
  canReact,
  masifiWaActionErrorMessage,
  ownReactionFor,
} from 'dashboard/helper/masifi/masifiWaMessageActions';
import { masifiWaActionRequest } from './masifiWaActionRequest';

// "Editar" y "Eliminar para todos" de WhatsApp QR (spec /root/docs/whatsapp-qr-edit-revoke-design.md §4.6) y
// "Reaccionar" (spec whatsapp-qr-polish-1 §3.7).
// MessageContextMenu.vue (upstream) lo monta dos veces: part="items" dentro del menú y part="dialogs" fuera de su v-if.
// El mensaje completo sale del store (conversation_id + id): Message.vue no se toca. El backend decide; esto solo oculta.
const props = defineProps({
  message: { type: Object, required: true },
  part: {
    type: String,
    default: 'items',
    validator: value => ['items', 'dialogs'].includes(value),
  },
});
const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const getConversationById = useMapGetter('getConversationById');
const getInbox = useMapGetter('inboxes/getInbox');
const getAccount = useMapGetter('accounts/getAccount');
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');

const conversationId = computed(
  () => props.message.conversation_id ?? props.message.conversationId
);
const conversation = computed(() =>
  getConversationById.value(conversationId.value)
);
const storeMessage = computed(
  () =>
    conversation.value?.messages?.find(item => item.id === props.message.id) ||
    null
);

const actionContext = () => ({
  message: storeMessage.value,
  conversation: conversation.value,
  inbox: getInbox.value(conversation.value?.inbox_id),
  features: getAccount.value(accountId.value)?.features || {},
  permissions: getUserPermissions(currentUser.value, accountId.value),
  userId: currentUser.value?.id,
  nowSeconds: Math.floor(Date.now() / 1000),
});
const showEdit = computed(
  () => props.part === 'items' && canEdit(actionContext())
);
const showDelete = computed(
  () => props.part === 'items' && canDeleteForEveryone(actionContext())
);
const showReact = computed(
  () => props.part === 'items' && canReact(actionContext())
);
// Nuestra reacción viva a este mensaje (para "Quitar mi reacción" y marcar el emoji actual).
const ownReaction = computed(() =>
  ownReactionFor(storeMessage.value, conversation.value?.messages || [])
);

const request = action => {
  masifiWaActionRequest.value = {
    action,
    conversationId: conversationId.value,
    messageId: props.message.id,
  };
  emit('close');
};

// --- part="dialogs": solo se renderiza un Dialog cuando hay un pedido para ESTE mensaje (cero <dialog> por burbuja).
const active = ref(null);
const dialogRef = ref(null);
const draft = ref('');
const isBusy = ref(false);
const original = computed(() => storeMessage.value?.content || '');
const canSave = computed(
  () => draft.value.trim().length > 0 && draft.value !== original.value
);

watch(masifiWaActionRequest, async pending => {
  if (props.part !== 'dialogs' || !pending) return;
  if (
    pending.messageId !== props.message.id ||
    pending.conversationId !== conversationId.value
  )
    return;
  masifiWaActionRequest.value = null;
  draft.value = original.value;
  active.value = pending.action;
  await nextTick();
  dialogRef.value?.open();
});

// T9-M1: cerrar (Esc, afuera, Cancelar) NO libera isBusy: con el pedido en vuelo, reabrir el diálogo lo muestra cargando
// y no permite un 2.º envío; lo libera el `finally` de submit cuando llega la respuesta.
const onDialogClose = () => {
  active.value = null;
};

const runAction = async () => {
  if (active.value === 'edit') {
    const { data } = await MasifiWhatsappQrMessagesAPI.edit(
      props.message.id,
      draft.value
    );
    store.commit(types.ADD_MESSAGE, data);
    return t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.EDIT_SUCCESS');
  }
  const { data } = await MasifiWhatsappQrMessagesAPI.deleteForEveryone(
    props.message.id
  );
  store.commit(types.ADD_MESSAGE, data);
  store.commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
  return t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.DELETE_SUCCESS');
};

// Un pedido a la vez (isBusy) para editar, eliminar y reaccionar; el error se muestra igual para los tres.
const perform = async run => {
  if (isBusy.value) return;
  isBusy.value = true;
  try {
    useAlert(await run());
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      masifiWaActionErrorMessage(error, {
        generic: t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.GENERIC_ERROR'),
        noAccess: t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.NO_ACCESS_ERROR'),
        unconfirmed: t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.UNCONFIRMED_ERROR'),
      })
    );
  } finally {
    isBusy.value = false;
  }
};
const submit = () => perform(runAction);
const react = emoji =>
  perform(async () => {
    const { data } = await MasifiWhatsappQrMessagesAPI.react(
      props.message.id,
      emoji
    );
    store.commit(types.ADD_MESSAGE, data);
    return emoji
      ? t('MASIFI_WHATSAPP_QR.REACT.SENT')
      : t('MASIFI_WHATSAPP_QR.REACT.REMOVED');
  });
</script>

<template>
  <template v-if="part === 'items'">
    <MenuItem
      v-if="showReact"
      :option="{
        icon: 'emoji-add',
        label: t('MASIFI_WHATSAPP_QR.REACT.MENU'),
      }"
      variant="icon"
      @click.stop="request('react')"
    />
    <MenuItem
      v-if="showEdit"
      :option="{
        icon: 'edit',
        label: t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.EDIT'),
      }"
      variant="icon"
      @click.stop="request('edit')"
    />
    <MenuItem
      v-if="showDelete"
      :option="{
        icon: 'delete',
        label: t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.DELETE_FOR_EVERYONE'),
      }"
      variant="icon"
      @click.stop="request('revoke')"
    />
  </template>
  <Dialog
    v-else-if="active === 'edit'"
    ref="dialogRef"
    type="edit"
    :title="t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.EDIT_DIALOG.TITLE')"
    :description="
      t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.EDIT_DIALOG.DESCRIPTION')
    "
    :confirm-button-label="
      t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.EDIT_DIALOG.SAVE')
    "
    :disable-confirm-button="!canSave"
    :is-loading="isBusy"
    @confirm="submit"
    @close="onDialogClose"
  >
    <TextArea
      :id="`masifi-wa-edit-${message.id}`"
      v-model="draft"
      :label="t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.EDIT_DIALOG.LABEL')"
      :max-length="MASIFI_WA_MAX_TEXT"
      :placeholder="
        t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.EDIT_DIALOG.PLACEHOLDER')
      "
      auto-height
      autofocus
    />
  </Dialog>
  <Dialog
    v-else-if="active === 'react'"
    ref="dialogRef"
    type="edit"
    :title="t('MASIFI_WHATSAPP_QR.REACT.DIALOG_TITLE')"
    :description="t('MASIFI_WHATSAPP_QR.REACT.DIALOG_DESCRIPTION')"
    :show-confirm-button="false"
    :is-loading="isBusy"
    @close="onDialogClose"
  >
    <div class="flex flex-col items-start gap-3">
      <div class="flex flex-wrap gap-2">
        <NextButton
          v-for="emoji in MASIFI_WA_QUICK_REACTIONS"
          :key="emoji"
          color="slate"
          :variant="emoji === ownReaction?.content ? 'solid' : 'faded'"
          :disabled="isBusy"
          @click="react(emoji)"
        >
          {{ emoji }}
        </NextButton>
      </div>
      <NextButton
        v-if="ownReaction"
        color="slate"
        variant="link"
        :disabled="isBusy"
        @click="react('')"
      >
        {{ t('MASIFI_WHATSAPP_QR.REACT.REMOVE') }}
      </NextButton>
    </div>
  </Dialog>
  <Dialog
    v-else-if="active === 'revoke'"
    ref="dialogRef"
    type="alert"
    :title="t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.DELETE_DIALOG.TITLE')"
    :description="
      t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.DELETE_DIALOG.DESCRIPTION')
    "
    :confirm-button-label="
      t('MASIFI_WHATSAPP_QR.MESSAGE_ACTIONS.DELETE_DIALOG.CONFIRM')
    "
    :is-loading="isBusy"
    @confirm="submit"
    @close="onDialogClose"
  />
</template>
